// ===========================================
// AuditLog — Journal d'activité (refonte UX)
// ===========================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Select, Tag, Grid, Button, Empty,
} from 'antd';
import {
    SearchOutlined, AuditOutlined, ClearOutlined, UserOutlined,
    LoginOutlined, LogoutOutlined, PlusCircleOutlined, EditOutlined,
    DeleteOutlined, ApiOutlined, GlobalOutlined,
} from '@ant-design/icons';
import { auditService } from '../../common/services';
import { getRoleLabel } from '../../common/auth/roleLabels';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './audit.page.scss';

const { Option } = Select;

const actionConfig = {
    create:     { label: 'Création',     color: 'green',   icon: <PlusCircleOutlined /> },
    update:     { label: 'Modification', color: 'blue',    icon: <EditOutlined /> },
    delete:     { label: 'Suppression',  color: 'red',     icon: <DeleteOutlined /> },
    login:      { label: 'Connexion',    color: 'purple',  icon: <LoginOutlined /> },
    logout:     { label: 'Déconnexion',  color: 'orange',  icon: <LogoutOutlined /> },
    page_visit: { label: 'Visite page',  color: 'default', icon: <GlobalOutlined /> },
    other:      { label: 'Autre',        color: 'default', icon: <ApiOutlined /> },
};

const methodColors = {
    POST: 'green', PUT: 'blue', PATCH: 'orange', DELETE: 'red', GET: 'default',
};

const AuditLog = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
    const [totals, setTotals] = useState({});
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const fetchLogs = async (page = 1, limit = 25) => {
        setLoading(true);
        try {
            const params = { page, limit, exclude: 'page_visit' };
            if (search) params.q = search;
            if (filterAction) params.action = filterAction;

            const res = await auditService.getList(params);
            const payload = res?.data || res;
            const items = payload?.items || [];
            setLogs(items);
            setPagination((prev) => ({
                ...prev,
                current: payload?.page || page,
                total: payload?.total || 0,
                pageSize: payload?.limit || limit,
            }));
            if (payload?.totals) setTotals(payload.totals);
        } catch (error) {
            console.error('Erreur chargement audit:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchLogs(1, pagination.pageSize); }, [search, filterAction]);

    // ─── KPIs ─────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const counts = logs.reduce((acc, l) => {
            acc[l.action] = (acc[l.action] || 0) + 1;
            return acc;
        }, {});
        return {
            total: pagination.total || logs.length,
            connexions: counts.login || 0,
            creations: counts.create || 0,
            suppressions: counts.delete || 0,
        };
    }, [logs, pagination.total]);

    const activeFiltersCount = (search ? 1 : 0) + (filterAction ? 1 : 0);
    const clearAllFilters = () => { setSearch(''); setFilterAction(''); };

    // ─── Colonnes ─────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'date',
            width: 170,
            fixed: isMobile ? false : 'left',
            render: (d) => d ? (
                <span className="audit-date">{new Date(d).toLocaleString('fr-FR')}</span>
            ) : '—',
        },
        {
            title: 'Utilisateur',
            key: 'user',
            width: 240,
            render: (_, r) => (
                <div className="audit-user-cell">
                    <div className="audit-user-avatar">
                        <UserOutlined />
                    </div>
                    <div className="audit-user-body">
                        <span className="audit-user-name">{r.adminName || 'N/A'}</span>
                        <span className="audit-user-meta">
                            {getRoleLabel(r.adminRole) || r.adminRole || ''}
                            {r.adminPhone ? ` · ${r.adminPhone}` : ''}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 140,
            render: (a) => {
                const conf = actionConfig[a] || { label: a || 'N/A', color: 'default', icon: null };
                return (
                    <Tag icon={conf.icon} color={conf.color} className="audit-action-tag">
                        {conf.label}
                    </Tag>
                );
            },
        },
        {
            title: 'Ressource',
            dataIndex: 'resource',
            key: 'resource',
            width: 130,
            render: (v) => v ? <span className="audit-resource">{v}</span> : '—',
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (v) => v || <span className="audit-muted">—</span>,
        },
        {
            title: 'Méthode',
            dataIndex: 'httpMethod',
            key: 'method',
            width: 100,
            align: 'center',
            render: (m) => m ? (
                <Tag color={methodColors[m] || 'default'} className="audit-method-tag">{m}</Tag>
            ) : '—',
        },
        {
            title: 'Statut',
            dataIndex: 'statusCode',
            key: 'status',
            width: 90,
            align: 'center',
            render: (s) => s ? (
                <Tag color={s >= 200 && s < 300 ? 'green' : 'red'} className="audit-status-tag">
                    {s}
                </Tag>
            ) : '—',
        },
        {
            title: 'IP',
            dataIndex: 'ipAddress',
            key: 'ip',
            width: 140,
            render: (v) => v ? <span className="audit-ip">{v}</span> : '—',
        },
    ];

    return (
        <div className="audit-page">
            {/* Header */}
            <header className="audit-header">
                <div className="audit-header-text">
                    <h1 className="audit-title">
                        <AuditOutlined /> Journal d'activité
                    </h1>
                    <p className="audit-subtitle">Traçabilité des actions effectuées dans le back-office</p>
                </div>
            </header>

            {/* KPIs */}
            <section className="audit-kpis" aria-label="Indicateurs journal d'activité">
                <MiniKpiCard
                    icon={<AuditOutlined />}
                    label="Entrées totales"
                    value={stats.total}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<LoginOutlined />}
                    label="Connexions"
                    value={stats.connexions}
                    tone="purple"
                />
                <MiniKpiCard
                    icon={<PlusCircleOutlined />}
                    label="Créations"
                    value={stats.creations}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<DeleteOutlined />}
                    label="Suppressions"
                    value={stats.suppressions}
                    tone="red"
                />
            </section>

            {/* Carte principale */}
            <Card className="audit-main-card">
                <div className="audit-toolbar">
                    <Input
                        placeholder="Rechercher par nom, téléphone, ressource…"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="audit-search"
                    />
                    <Select
                        value={filterAction || undefined}
                        onChange={(v) => setFilterAction(v || '')}
                        className="audit-filter"
                        placeholder="Toutes les actions"
                        allowClear
                    >
                        <Option value="create">Création</Option>
                        <Option value="update">Modification</Option>
                        <Option value="delete">Suppression</Option>
                        <Option value="login">Connexion</Option>
                        <Option value="logout">Déconnexion</Option>
                    </Select>
                    {activeFiltersCount > 0 && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="audit-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                </div>

                <Table
                    dataSource={logs}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucune entrée dans le journal" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: (p, ps) => fetchLogs(p, ps),
                        showSizeChanger: true,
                        showTotal: (t) => `${t} entrée${t > 1 ? 's' : ''}`,
                        pageSizeOptions: ['10', '25', '50', '100'],
                    }}
                />
            </Card>
        </div>
    );
};

export default AuditLog;
